import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MailSettingsCard } from "@/components/MailSettingsCard";
import { WebsiteApiCard } from "@/components/WebsiteApiCard";
import { ChannelsCard } from "@/components/ChannelsCard";
import { KeyRound, UserPlus, Trash2, MailCheck, RefreshCw, PlayCircle, CheckCircle2, XCircle, AlertCircle } from "lucide-react";

// Zeiterfassungs-Abteilungen (müssen zu DEPARTMENTS in Schichtplan.jsx passen)
const DEPARTMENTS = ["Housekeeping", "Küche", "Verwaltung", "Guest Relation", "Hausmeister"];
const DEPARTMENT_EMOJIS = { "Housekeeping": "🧹", "Küche": "🍳", "Verwaltung": "📋", "Guest Relation": "🤝", "Hausmeister": "🔧" };

const ROLE_LABEL = { admin: "Admin", staff: "Mitarbeiter", steuerberater: "Steuerberater" };

export default function Settings() {
  const [c, setC] = useState(null);
  const [users, setUsers] = useState([]);

  // Auto-Mail-Diagnose
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [running, setRunning] = useState(false);

  // New-user dialog
  const [newOpen, setNewOpen] = useState(false);
  const [nu, setNu] = useState({ name: "", email: "", role: "staff", password: "", departments: [] });
  const [savingNew, setSavingNew] = useState(false);

  // Edit departments dialog
  const [deptUser, setDeptUser] = useState(null);
  const [deptSel, setDeptSel] = useState([]);
  const [savingDept, setSavingDept] = useState(false);

  // Password-reset dialog
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const loadUsers = () => api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  const loadDiag = () => {
    setDiagLoading(true);
    api.get("/admin/auto-mail-diagnose")
      .then(r => setDiag(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Diagnose fehlgeschlagen"))
      .finally(() => setDiagLoading(false));
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/auto-mail-run-now");
      toast.success(`Test abgeschlossen: ${r.data.total_sent} Mails versendet`);
      loadDiag();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test fehlgeschlagen");
    } finally { setRunning(false); }
  };

  useEffect(() => {
    api.get("/settings/company").then(r => setC(r.data));
    loadUsers();
    loadDiag();
  }, []);

  const save = async () => {
    try { await api.put("/settings/company", c); toast.success("Gespeichert"); }
    catch { toast.error("Fehler"); }
  };

  const createUser = async () => {
    if (!nu.name.trim() || !nu.email.trim() || !nu.password) {
      toast.error("Bitte Name, E-Mail und Passwort ausfüllen.");
      return;
    }
    if (nu.password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    setSavingNew(true);
    try {
      await api.post("/users", nu);
      toast.success(`Benutzer ${nu.email} angelegt.`);
      setNewOpen(false);
      setNu({ name: "", email: "", role: "staff", password: "", departments: [] });
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Anlegen.");
    } finally { setSavingNew(false); }
  };

  const openDeptDialog = (u) => {
    setDeptUser(u);
    setDeptSel(u.departments || []);
  };
  const saveDepartments = async () => {
    if (!deptUser) return;
    setSavingDept(true);
    try {
      await api.put(`/users/${deptUser.id}/departments`, { departments: deptSel });
      toast.success(`Abteilungen für ${deptUser.name} aktualisiert`);
      setDeptUser(null); setDeptSel([]);
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern.");
    } finally { setSavingDept(false); }
  };

  const savePassword = async () => {
    if (!pwUser) return;
    if (pwValue.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    setSavingPw(true);
    try {
      await api.put(`/users/${pwUser.id}/password`, { password: pwValue });
      toast.success(`Passwort für ${pwUser.email} aktualisiert.`);
      setPwUser(null);
      setPwValue("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern.");
    } finally { setSavingPw(false); }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(`2FA für ${u.email} zurücksetzen? Der Benutzer muss den Authenticator beim nächsten Login neu einrichten.`)) return;
    try {
      await api.post(`/users/${u.id}/2fa/reset`);
      toast.success("2FA zurückgesetzt.");
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Zurücksetzen.");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Benutzer ${u.email} wirklich löschen?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Benutzer gelöscht.");
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Löschen.");
    }
  };

  if (!c) return null;

  const F = (k, label) => (
    <div><Label>{label}</Label>
      <Input value={c[k] || ""} onChange={e => setC({ ...c, [k]: e.target.value })} className="rounded-xl mt-1.5" data-testid={`settings-${k}`} /></div>
  );

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: "'Work Sans',sans-serif" }}>Einstellungen</h1>
        <p className="text-muted-foreground mt-1">Firmendaten, Bankverbindung, Rechnungseinstellungen</p>
      </div>
      <MailSettingsCard />
      <WebsiteApiCard />
      <ChannelsCard />

      <div className="msb-card">
        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: "'Work Sans',sans-serif" }}>
              <MailCheck className="w-5 h-5 text-primary" />
              Auto-Mail-Diagnose
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Zeigt Live-Status der automatischen Gast-Mails. „Test jetzt starten" feuert alle 4 täglichen Crons sofort (idempotent — nichts wird doppelt versendet).
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={loadDiag} disabled={diagLoading}
                    className="rounded-lg gap-1.5" data-testid="diag-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${diagLoading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
            <Button size="sm" onClick={runNow} disabled={running || diagLoading}
                    className="rounded-lg gap-1.5 bg-primary" data-testid="diag-run-now">
              <PlayCircle className="w-3.5 h-3.5" />
              {running ? "Läuft…" : "Test jetzt starten"}
            </Button>
          </div>
        </div>

        {!diag && diagLoading && <div className="text-sm text-muted-foreground py-4">Lade Diagnose…</div>}
        {diag && (
          <div className="space-y-4">
            {/* Mail-Versand-Status */}
            <div className={`rounded-xl border p-3 flex items-center gap-3 ${diag.gmail?.connected ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
                 data-testid="diag-gmail-status">
              {diag.gmail?.connected
                ? <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                : <XCircle className="w-6 h-6 text-red-600 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="font-semibold">
                  {diag.gmail?.connected ? `Mail-Versand aktiv über ${diag.gmail.email}` : "❌ Mail-Versand NICHT möglich — Mails werden NICHT versendet!"}
                </div>
                {!diag.gmail?.connected && (
                  <div className="text-xs text-red-700 mt-0.5">
                    → Strato-Postfach prüfen (Einstellungen → E-Mail-Postfach). {diag.gmail?.error || ""}
                  </div>
                )}
              </div>
            </div>

            {/* Cron-Übersicht */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(diag.crons || []).map(c => {
                const candidates = diag.candidates?.[c.kind] || [];
                const wouldSend = candidates.filter(x => !x.skip);
                const wouldSkip = candidates.filter(x => x.skip);
                return (
                  <div key={c.kind} className="rounded-xl border border-border bg-muted/30 p-3" data-testid={`diag-cron-${c.kind}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <div className="font-semibold text-sm">{c.label}</div>
                        <div className="text-[11px] text-muted-foreground">{c.when}</div>
                      </div>
                      {c.last?.sent_at ? (
                        <div className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold whitespace-nowrap">
                          zuletzt {new Date(c.last.sent_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      ) : (
                        <div className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-semibold whitespace-nowrap flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> noch NIE versendet
                        </div>
                      )}
                    </div>
                    {c.kind !== "booking_confirmation" && (
                      <>
                        {candidates.length === 0 && (
                          <div className="text-[11px] text-muted-foreground italic">Keine passenden Buchungen aktuell</div>
                        )}
                        {wouldSend.length > 0 && (
                          <div className="text-[11px] mt-1">
                            <span className="font-semibold text-emerald-700">✓ Würde jetzt senden:</span>{" "}
                            {wouldSend.map(x => x.guest || x.email).join(", ")}
                          </div>
                        )}
                        {wouldSkip.length > 0 && (
                          <div className="text-[11px] mt-1 text-muted-foreground">
                            <span className="font-semibold">Übersprungen:</span>{" "}
                            {wouldSkip.map(x => `${x.guest || "?"} (${x.skip})`).join(" · ")}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="msb-card">
        <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: "'Work Sans',sans-serif" }}>Firmendaten</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {F("name", "Firmenname")}{F("owner", "Inhaber")}
          {F("street", "Straße")}{F("postal_code", "PLZ")}
          {F("city", "Ort")}{F("country", "Land")}
          {F("phone", "Telefon")}{F("email", "E-Mail")}
          {F("vat_id", "USt-IdNr.")}{F("tax_id", "Steuernummer")}
        </div>
      </div>

      <div className="msb-card">
        <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: "'Work Sans',sans-serif" }}>Bankverbindung</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {F("bank", "Bank")}{F("iban", "IBAN")}
          {F("bic", "BIC")}{F("purpose", "Standard-Verwendungszweck")}
        </div>
      </div>

      <div className="msb-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ fontFamily: "'Work Sans',sans-serif" }}>Benutzer</h3>
          <Button
            onClick={() => setNewOpen(true)}
            className="rounded-xl bg-primary gap-2"
            data-testid="add-user-btn"
          >
            <UserPlus className="w-4 h-4" /> Neuen Benutzer anlegen
          </Button>
        </div>
        <div className="divide-y divide-border">
          {users.map(u => (
            <div key={u.id} className="py-3 flex flex-wrap items-center gap-3 justify-between" data-testid={`user-row-${u.email}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{u.name}</div>
                <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                <div className={`text-[10px] mt-1 ${u.totp_enabled ? "text-green-700" : "text-amber-700"}`} data-testid={`user-2fa-status-${u.email}`}>
                  {u.totp_enabled ? "2FA aktiv" : "2FA noch nicht eingerichtet"}
                </div>
                {(u.departments && u.departments.length > 0) && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {u.departments.map(d => (
                      <span key={d} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                        {DEPARTMENT_EMOJIS[d]} {d}
                      </span>
                    ))}
                  </div>
                )}
                {u.role !== "steuerberater" && (!u.departments || u.departments.length === 0) && (
                  <div className="text-[10px] text-amber-700 mt-1 italic">⚠️ Keine Abteilungen zugewiesen</div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">
                  {ROLE_LABEL[u.role] || u.role}
                </span>
                {u.role !== "steuerberater" && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => openDeptDialog(u)}
                    className="rounded-lg gap-1.5"
                    data-testid={`edit-dept-${u.email}`}
                  >
                    🏷️ Abteilungen
                  </Button>
                )}
                <Button
                  variant="outline" size="sm"
                  onClick={() => { setPwUser(u); setPwValue(""); }}
                  className="rounded-lg gap-1.5"
                  data-testid={`change-pw-${u.email}`}
                >
                  <KeyRound className="w-3.5 h-3.5" /> Passwort
                </Button>
                {u.totp_enabled && (
                  <Button variant="outline" size="sm" onClick={() => reset2fa(u)} className="rounded-lg" data-testid={`reset-2fa-${u.email}`}>
                    2FA zurücksetzen
                  </Button>
                )}
                <Button
                  variant="ghost" size="sm"
                  onClick={() => removeUser(u)}
                  className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-user-${u.email}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <div className="py-4 text-sm text-muted-foreground">Keine Benutzer vorhanden.</div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} className="rounded-xl bg-primary" data-testid="save-settings-btn">Speichern</Button>
      </div>

      {/* Create user dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-user-dialog">
          <DialogHeader>
            <DialogTitle>Neuen Benutzer anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={nu.name} onChange={e => setNu({ ...nu, name: e.target.value })} className="rounded-xl mt-1.5" data-testid="new-user-name" />
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input type="email" value={nu.email} onChange={e => setNu({ ...nu, email: e.target.value })} className="rounded-xl mt-1.5" data-testid="new-user-email" />
            </div>
            <div>
              <Label>Rolle</Label>
              <Select value={nu.role} onValueChange={v => setNu({ ...nu, role: v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (voller Zugriff)</SelectItem>
                  <SelectItem value="staff">Mitarbeiter</SelectItem>
                  <SelectItem value="steuerberater">Steuerberater (nur Portal)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Passwort</Label>
              <Input type="text" value={nu.password} onChange={e => setNu({ ...nu, password: e.target.value })} className="rounded-xl mt-1.5" placeholder="mind. 6 Zeichen" data-testid="new-user-password" />
            </div>
            {nu.role !== "steuerberater" && (
              <div>
                <Label>Abteilungen für Zeiterfassung</Label>
                <div className="text-[11px] text-muted-foreground mb-2">
                  Welche Bereiche deckt der Mitarbeiter ab? Bei nur einer Abteilung wird der Dienst-Start-Picker übersprungen.
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {DEPARTMENTS.map(d => {
                    const checked = (nu.departments || []).includes(d);
                    return (
                      <label key={d} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition ${checked ? "bg-primary/10 border-primary" : "border-border hover:bg-muted/40"}`}>
                        <input type="checkbox" checked={checked}
                               onChange={() => setNu(prev => ({
                                 ...prev,
                                 departments: checked
                                   ? (prev.departments || []).filter(x => x !== d)
                                   : [...(prev.departments || []), d]
                               }))}
                               data-testid={`new-user-dept-${d}`}/>
                        <span className="text-sm">{DEPARTMENT_EMOJIS[d]} {d}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)} className="rounded-xl">Abbrechen</Button>
            <Button onClick={createUser} disabled={savingNew} className="rounded-xl bg-primary" data-testid="new-user-save">
              {savingNew ? "Speichere…" : "Anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit departments dialog */}
      <Dialog open={!!deptUser} onOpenChange={o => { if (!o) { setDeptUser(null); setDeptSel([]); } }}>
        <DialogContent data-testid="edit-dept-dialog">
          <DialogHeader>
            <DialogTitle>Abteilungen zuweisen</DialogTitle>
          </DialogHeader>
          {deptUser && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Welche Abteilungen deckt <strong>{deptUser.name}</strong> ab?<br/>
                <span className="text-xs">Bei nur einer Abteilung startet der Dienst direkt ohne Nachfrage.</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {DEPARTMENTS.map(d => {
                  const checked = deptSel.includes(d);
                  return (
                    <label key={d} className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition ${checked ? "bg-primary/10 border-primary" : "border-border hover:bg-muted/40"}`}>
                      <input type="checkbox" checked={checked}
                             onChange={() => setDeptSel(checked ? deptSel.filter(x => x !== d) : [...deptSel, d])}
                             data-testid={`dept-check-${d}`}/>
                      <span className="text-sm font-medium">{DEPARTMENT_EMOJIS[d]} {d}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeptUser(null); setDeptSel([]); }} className="rounded-xl">Abbrechen</Button>
            <Button onClick={saveDepartments} disabled={savingDept} className="rounded-xl bg-primary" data-testid="save-dept">
              {savingDept ? "Speichere…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog open={!!pwUser} onOpenChange={o => { if (!o) { setPwUser(null); setPwValue(""); } }}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle>Passwort ändern</DialogTitle>
          </DialogHeader>
          {pwUser && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Für <strong>{pwUser.name}</strong> ({pwUser.email})
              </div>
              <div>
                <Label>Neues Passwort</Label>
                <Input type="text" value={pwValue} onChange={e => setPwValue(e.target.value)} className="rounded-xl mt-1.5" placeholder="mind. 6 Zeichen" data-testid="reset-pw-input" autoFocus />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwUser(null); setPwValue(""); }} className="rounded-xl">Abbrechen</Button>
            <Button onClick={savePassword} disabled={savingPw} className="rounded-xl bg-primary" data-testid="reset-pw-save">
              {savingPw ? "Speichere…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
